import React, { useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { useAtom, useAtomValue } from 'jotai';
import { DimensionWidgetWrapper, StyledTabs } from './styled';
import DimensionTables from './DimensionTables';
import { Tabs } from '@store/trends/base/reducer';
import ViewerSessions from './ViewerSessions';
import AppUserSessions from '@components/app-viewer-sessions';
import { AppAnalyseNetworkRequest } from '@components/app-analyse-network-request';
import ImpactedViewers from './impactedViewers';
import { dimensionPanelAtom } from '@src/atoms/panels';
import ErrorBoundary from '@components/common/Retry/ErrorBoundary';
import commonSelector, {
  ovbViewersMetricsEnabledSelector,
  realTimeModeOnTrendsSelector,
  isCDNAccountSelector,
} from '@store/common/selectors';
import featureNames from '@utils/featureToggle/constants';
import { isAppModule } from '@utils/constants';
import { horizontalLayoutAtom } from '@components/metrics-panel-hz/atoms';
import { activeTabAtom } from './atoms';
import { useCurrentLimitation } from '@src/hooks/useCurrentLimitation';
import ViewerAccessGate from '@components/common/ViewerAccessGate';
import Builders from './header/Builders';
import TableCompare from './TableCompare';
import { useCurrentFlowConfig } from '@src/hooks/flow/useFlowConfigs';
import { DIMENSION_WRAPPER_ID, checkFlowNWRRCAAvaliable, shouldShowSessionsTab } from './utils';

function DimensionPanel() {
  const [activeTab, setActiveTab] = useAtom(activeTabAtom);
  const showImpactedViewersTab = useSelector<any, boolean>(ovbViewersMetricsEnabledSelector);
  const isCDNAccount = useSelector<any, boolean>(isCDNAccountSelector);
  const isLiveMode = useSelector<any, boolean>(realTimeModeOnTrendsSelector);
  const { features } = useSelector(commonSelector);
  const horizontalLayout = useAtomValue(horizontalLayoutAtom);
  const flowConfig = useCurrentFlowConfig();
  const limitation = useCurrentLimitation();

  const disableNWTab = get(limitation, ['dimensionPanel', 'networkRequestTab', 'disabled'], false);
  const height = horizontalLayout ? 'auto' : '100%';

  // show when any of the flag is enabled
  const showViewersTab =
    features[featureNames.VIEWER_DRILLDOWN] ||
    features[featureNames.VIEWER_DRILLDOWN_REST_METRICS] ||
    features[featureNames.VIEWER_DRILLDOWN_DISTRIBUTION];

  useEffect(() => {
    if (isLiveMode) {
      // only dimension tab is available in live mode
      setActiveTab(Tabs.TopTrends);
    }
  }, [isLiveMode, setActiveTab]);

  const handleTabChange = useCallback(
    (key: Tabs) => {
      setActiveTab(key);
    },
    [setActiveTab],
  );

  const tabList = useMemo(() => {
    const items: {
      key: Tabs;
      label: React.ReactNode;
      children: React.ReactNode;
      disabled: boolean;
    }[] = [
      {
        key: Tabs.TopTrends,
        label: 'Trends',
        children: <DimensionTables hiddenDimensions={limitation?.dimensions?.hiddenKeys} />,
        disabled: false,
      },
    ];

    // Viewer/user-level tabs (Sessions, Viewers, App Users/Replays) are gated per-user on
    // hasViewerAccess (PDF-1524 compliance). The tab stays visible per its existing feature/account
    // condition; a user without the permission sees a "no permission" message INSIDE the tab (not
    // the data) via ViewerAccessGate. The server (requireViewerAccess) is the real enforcement.
    if (shouldShowSessionsTab({ showViewersTab, isCDNAccount })) {
      items.push({
        key: Tabs.ViewerSessions,
        label: 'Sessions',
        children: (
          <ViewerAccessGate>
            <ViewerSessions />
          </ViewerAccessGate>
        ),
        disabled: isLiveMode,
      });
    }

    if (showImpactedViewersTab && !isCDNAccount) {
      items.push({
        key: Tabs.TopImpactedViewers,
        label: 'Viewers',
        children: (
          <ViewerAccessGate>
            <ImpactedViewers />
          </ViewerAccessGate>
        ),
        disabled: isLiveMode,
      });
    }

    if (isAppModule) {
      items.push({
        key: Tabs.AppUserSessions,
        label: 'Users',
        children: (
          <ViewerAccessGate>
            <AppUserSessions />
          </ViewerAccessGate>
        ),
        disabled: isLiveMode,
      });

      // Network Request Tab is only available for DPI old Data Model,
      // or DPI new Data Model but without configurated flow network metrics
      if (!flowConfig || checkFlowNWRRCAAvaliable(flowConfig)) {
        items.push({
          key: Tabs.AppAnalyseNetworkRequest,
          label: 'Network Request',
          children: <AppAnalyseNetworkRequest />,
          disabled: isLiveMode || disableNWTab,
        });
      }
    }

    return items;
  }, [
    flowConfig,
    isLiveMode,
    showViewersTab,
    showImpactedViewersTab,
    disableNWTab,
    limitation,
    isCDNAccount,
  ]);

  return (
    <DimensionWidgetWrapper id={DIMENSION_WRAPPER_ID} height={height}>
      <StyledTabs
        activeKey={activeTab}
        items={tabList}
        onChange={handleTabChange}
        destroyOnHidden
      />
      <TableCompare activeTab={activeTab} />
    </DimensionWidgetWrapper>
  );
}

const DimensionPanelContainer = () => {
  const showPanel = useAtomValue(dimensionPanelAtom);
  return showPanel ? (
    <ErrorBoundary>
      <DimensionPanel />
      <Builders />
    </ErrorBoundary>
  ) : null;
};

export default DimensionPanelContainer;
